// client/src/components/order/ReorderModal.js
import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, TrendingUp, TrendingDown, Package, ShoppingCart, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import PriceComparisonTable from './PriceComparisonTable';
import { useCart } from '../../contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { productDiscountsAPI, ordersAPI } from '../../services/api';

const ReorderModal = ({ isOpen, onClose, reorderData, originalOrder }) => {
  const { t } = useTranslation(['business', 'common']);
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState({});
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showFullComparison, setShowFullComparison] = useState(false);
  const [adding, setAdding] = useState(false);
  const [myProductDiscounts, setMyProductDiscounts] = useState({}); // { productId: { percentage, reason } }
  const [erpPricing, setErpPricing] = useState(null); // { lines: [...], totals: {...} }
  const [erpLoading, setErpLoading] = useState(false);
  const erpTimerRef = useRef(null);

  // Fetch product discounts for this wholesaler
  useEffect(() => {
    if (!reorderData?.wholesaler?._id) return;
    const fetchPD = async () => {
      try {
        const res = await productDiscountsAPI.getMyDiscounts(String(reorderData.wholesaler._id));
        if (res.data.success && res.data.discounts && typeof res.data.discounts === 'object') {
          setMyProductDiscounts(res.data.discounts);
        }
      } catch (e) { /* silent */ }
    };
    fetchPD();
  }, [reorderData?.wholesaler?._id]);

  // Initialize quantities and selection when reorderData changes
  useEffect(() => {
    if (reorderData?.availableItems) {

      const initialQuantities = {};
      const initialSelection = new Set();
      
      reorderData.availableItems.forEach(item => {
        const key = typeof item.productId === 'object' ? item.productId.toString() : String(item.productId);
        initialQuantities[key] = item.quantity;
        
        // Auto-select available items (stock > 0)
        if (item.stock > 0) {
          initialSelection.add(key);
        }
      });
      
      setQuantities(initialQuantities);
      setSelectedItems(initialSelection);
    }
  }, [reorderData]);

  // ERP real-time pricing (debounced) — fires when quantities or selection change
  const isErpEnabled = reorderData?.wholesaler?.erpIntegration?.isActive;
  useEffect(() => {
    if (!isErpEnabled || !reorderData?.availableItems || !reorderData?.wholesaler?._id) return;

    // Build items list from selected items with current quantities
    const itemsForErp = reorderData.availableItems
      .filter(item => {
        const key = typeof item.productId === 'object' ? item.productId.toString() : String(item.productId);
        return selectedItems.has(key) && (quantities[key] || 0) > 0;
      })
      .map(item => {
        const key = typeof item.productId === 'object' ? item.productId.toString() : String(item.productId);
        return { productId: key, quantity: quantities[key] || 0 };
      });

    if (itemsForErp.length === 0) {
      setErpPricing(null);
      return;
    }

    setErpLoading(true);
    if (erpTimerRef.current) clearTimeout(erpTimerRef.current);

    erpTimerRef.current = setTimeout(async () => {
      try {
        const res = await ordersAPI.calculateErp({
          wholesalerId: String(reorderData.wholesaler._id),
          items: itemsForErp
        });
        if (res.data?.erpAvailable && res.data.pricing) {
          setErpPricing(res.data.pricing);
        } else {
          setErpPricing(null);
        }
      } catch (err) {
        // Silent fallback to platform pricing
        setErpPricing(null);
      } finally {
        setErpLoading(false);
      }
    }, 800);

    return () => { if (erpTimerRef.current) clearTimeout(erpTimerRef.current); };
  }, [isErpEnabled, selectedItems, quantities, reorderData?.availableItems, reorderData?.wholesaler?._id]);

  // Early return AFTER hooks
  if (!isOpen || !reorderData || !reorderData.availableItems) return null;

  const {
    originalOrder: orderInfo,
    wholesaler,
    reorderSummary,
    availableItems: items,  // ✅ FIXED: Rename availableItems to items for easier usage
    unavailableItems,
    priceChanges,
    totals,
    delivery,
    paymentMethod
  } = reorderData;

  const hidePricing = wholesaler?.hidePricing;

  // Helper: get ERP line for a product
  const getErpLine = (productId) => {
    if (!erpPricing?.lines) return null;
    const key = typeof productId === 'object' ? productId.toString() : String(productId);
    return erpPricing.lines.find(l => l.productId === key) || null;
  };

  // Calculate adjusted totals based on quantity changes AND selection
  // Uses ERP pricing when available, falls back to platform pricing
  const calculateAdjustedTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    items.forEach(item => {
      const key = typeof item.productId === 'object' ? item.productId.toString() : String(item.productId);
      const qty = quantities[key] || 0;

      if (selectedItems.has(key)) {
        const erpLine = getErpLine(item.productId);
        if (erpLine) {
          // ERP pricing: unitPrice is the base price, lineTotal is after discounts
          const lineGross = erpLine.unitPrice * qty;
          subtotal += lineGross;
          // lineTotal already accounts for compound discounts — scale by qty ratio
          const scaledLineTotal = (erpLine.lineTotal / (erpLine.quantity || 1)) * qty;
          totalDiscount += lineGross - scaledLineTotal;
        } else {
          subtotal += item.currentPrice * qty;
          // Apply product discount if available
          const pd = myProductDiscounts[key];
          if (pd) totalDiscount += item.currentPrice * qty * (pd.percentage / 100);
        }
      }
    });

    const deliveryFee = parseFloat(totals.deliveryFee) || 0;
    const total = subtotal - totalDiscount + deliveryFee;

    return {
      subtotal,
      totalDiscount,
      deliveryFee,
      total
    };
  };

  const adjustedTotals = calculateAdjustedTotals();

  // Stock status helpers
  const getStockStatus = (item) => {
    // Use the stockStatus from product if available
    if (item.stockStatus) {
      switch (item.stockStatus) {
        case 'out_of_stock': return 'out';
        case 'low_stock': return 'low';
        case 'in_stock': return 'available';
        default: return 'available';
      }
    }
    
    // Fallback to quantity-based logic
    const stock = item.stock || 0;
    if (stock === 0) return 'out';
    if (stock <= 10) return 'low';
    return 'available';
  };

  const getStockDot = (status) => {
    const colors = {
      available: 'bg-green-500',
      low: 'bg-yellow-500',
      out: 'bg-red-500'
    };
    
    return (
      <div className={`w-3 h-3 rounded-full ${colors[status] || 'bg-gray-500'}`} />
    );
  };

  const getStockBadgeClass = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'low': return 'bg-yellow-100 text-yellow-800';
      case 'out': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Toggle item selection
  const toggleItemSelection = (key) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Select/Deselect all available items
  const toggleSelectAll = () => {
    if (selectedItems.size === items.filter(i => i.stock > 0).length) {
      // Deselect all
      setSelectedItems(new Set());
    } else {
      // Select all available
      const allAvailable = new Set();
      items.forEach(item => {
        const key = String(item.productId);
        if (item.stock > 0) {
          allAvailable.add(key);
        }
      });
      setSelectedItems(allAvailable);
    }
  };

  const handleQuantityChange = (productId, value) => {
    const key = typeof productId === 'object' ? productId.toString() : String(productId);
    const numValue = parseInt(value) || 0;
    if (numValue >= 0 && numValue <= 999) {
      setQuantities(prev => ({
        ...prev,
        [key]: numValue
      }));
    }
  };

  const handleAddToCart = () => {
    const itemsToAdd = items.filter(item => {
      const key = typeof item.productId === 'object' ? item.productId.toString() : String(item.productId);
      return selectedItems.has(key) && quantities[key] > 0;
    });
    
    if (itemsToAdd.length === 0) {
      toast(t('business:reorder.modal.validation.noProducts') || 'Please select at least one product');
      return;
    }

    setAdding(true);
    
    try {
      // Add each item to cart
      let addedCount = 0;
      itemsToAdd.forEach(item => {
        const key = typeof item.productId === 'object' ? item.productId.toString() : String(item.productId);
        const qty = quantities[key];
        
        if (qty > 0) {
          addToCart({
            _id: item.productId,
            name: item.name,
            pricing: {
              unitPrice: item.currentPrice,
              unit: item.unit
            },
            wholesalerId: wholesaler._id,
            wholesalerName: wholesaler.companyName,
            images: item.images || []
          }, qty);
          addedCount++;
        }
      });
      
      // Show success and navigate
      setTimeout(() => {
        setAdding(false);
        onClose();
        navigate('/cart');
      }, 500);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      setAdding(false);
      toast.error(t('business:reorder.modal.errors.submitFailed') || 'Failed to add items to cart');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-1">
                {t('business:reorder.modal.title', { orderNumber: orderInfo?.orderNumber || '' })}
              </h2>
              <p className="text-blue-100 text-sm">
                {t('business:reorder.modal.subtitle', { 
                  date: orderInfo?.createdAt ? format(new Date(orderInfo.createdAt), 'MMMM d, yyyy') : '' 
                })}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ERP Pricing Banner */}
          {isErpEnabled && !hidePricing && (
            <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-center gap-2 text-sm text-blue-700">
              {erpLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('business:cart.erpPricingLoading', 'Calculating personalized pricing...')}
                </>
              ) : erpPricing ? (
                <>
                  <span className="font-medium">✓</span>
                  {t('business:cart.erpPricingApplied', 'Personalized pricing applied')}
                </>
              ) : (
                <>
                  <span>ℹ</span>
                  {t('business:cart.erpPricingInfo', 'Personalized pricing available — prices will update shortly')}
                </>
              )}
            </div>
          )}

          {/* Price Changes Warning */}
          {!hidePricing && priceChanges?.hasChanges && (
            <div className={`mb-6 p-4 rounded-lg border-2 ${
              priceChanges.totalIncrease > 0 
                ? 'bg-orange-50 border-orange-200' 
                : 'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                  priceChanges.totalIncrease > 0 ? 'text-orange-500' : 'text-green-500'
                }`} />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    {priceChanges.totalIncrease > 0 
                      ? t('business:reorder.modal.warnings.priceIncrease')
                      : t('business:reorder.modal.priceComparison.priceDecreased', { percent: Math.abs(priceChanges.percentageChange || 0).toFixed(1) })
                    }
                  </h4>
                  <p className="text-sm text-gray-600">
                    {priceChanges.itemsIncreased > 0 && (
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-4 h-4 text-red-500" />
                        {priceChanges.itemsIncreased} items increased
                      </span>
                    )}
                    {priceChanges.itemsDecreased > 0 && (
                      <span className="flex items-center gap-1 ml-4">
                        <TrendingDown className="w-4 h-4 text-green-500" />
                        {priceChanges.itemsDecreased} items decreased
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Unavailable Items Warning */}
          {unavailableItems && unavailableItems.length > 0 && (
            <div className="mb-6 p-4 rounded-lg border-2 bg-red-50 border-red-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 mt-0.5 text-red-500" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    {t('business:reorder.modal.warnings.unavailableProducts')}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {unavailableItems.map(item => item.name).join(', ')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Products Table */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('business:reorder.modal.products.title')}
              </h3>
              <button
                onClick={toggleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {selectedItems.size === items.filter(i => i.stock > 0).length 
                  ? t('business:reorder.modal.products.deselectAll')
                  : t('business:reorder.modal.products.selectAll')
                }
              </button>
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Select
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('business:reorder.modal.products.product')}
                    </th>
                    {!hidePricing && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {t('business:reorder.modal.products.price')}
                    </th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {t('business:reorder.modal.products.quantity', 'Quantity')}
                    </th>
                    {!hidePricing && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      {t('business:reorder.modal.products.subtotal')}
                    </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => {
                    const key = typeof item.productId === 'object' ? item.productId.toString() : String(item.productId);
                    const qty = quantities[key] || 0;
                    const stockStatus = getStockStatus(item);
                    const isSelected = selectedItems.has(key);
                    const isDisabled = stockStatus === 'out';
                    
                    return (
                      <tr key={key} className={isDisabled ? 'bg-red-50 opacity-75' : ''}>
                        {/* Checkbox column */}
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleItemSelection(key)}
                            disabled={isDisabled}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </td>
                        
                        {/* Product name with stock indicator */}
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <div className="mt-1">{getStockDot(stockStatus)}</div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{item.name}</p>
                            {item.sku && (
                              <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                            )}
                            
                            {/* Stock status message */}
                            {stockStatus === 'low' && (
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${getStockBadgeClass(stockStatus)}`}>
                                ⚠️ Low stock
                              </span>
                            )}
                            {stockStatus === 'out' && (
                              <span className={`inline-block mt-1 px-3 py-1 rounded-md text-sm ${getStockBadgeClass(stockStatus)}`}>
                                ❌ Out of stock
                              </span>
                            )}
                            
                            {!hidePricing && item.priceChanged && (
                              <p className={`text-xs mt-1 ${
                                item.priceIncreased ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {item.priceIncreased
                                  ? t('business:reorder.modal.priceComparison.priceIncreased', {
                                      percent: Math.abs(item.priceChangePercent)
                                    })
                                  : t('business:reorder.modal.priceComparison.priceDecreased', {
                                      percent: Math.abs(item.priceChangePercent)
                                    })
                                }
                              </p>
                            )}
                            </div>
                          </div>
                        </td>
                        {!hidePricing && (
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            const erpLine = getErpLine(item.productId);
                            return (
                              <div>
                                {erpLine ? (
                                  <>
                                    <p className="font-medium">€{parseFloat(erpLine.unitPrice).toFixed(2)}</p>
                                    {erpLine.unitPrice !== item.currentPrice && (
                                      <p className="text-xs text-gray-500 line-through">
                                        €{parseFloat(item.currentPrice).toFixed(2)}
                                      </p>
                                    )}
                                    {erpLine.discountPct > 0 && (
                                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded-full">
                                        -{erpLine.discountPct}%
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <p className="font-medium">€{parseFloat(item.currentPrice).toFixed(2)}</p>
                                    {item.priceChanged && (
                                      <p className="text-xs text-gray-500 line-through">
                                        €{parseFloat(item.originalPrice).toFixed(2)}
                                      </p>
                                    )}
                                    {myProductDiscounts[key] && (
                                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">
                                        -{myProductDiscounts[key].percentage}%
                                      </span>
                                    )}
                                  </>
                                )}
                                {erpLoading && (
                                  <Loader2 className="w-3 h-3 animate-spin text-blue-400 inline-block ml-1" />
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleQuantityChange(item.productId, qty - 1)}
                              disabled={isDisabled}
                              className="w-8 h-8 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={qty}
                              onChange={(e) => handleQuantityChange(item.productId, e.target.value)}
                              disabled={isDisabled}
                              className="w-16 text-center border border-gray-300 rounded-lg px-2 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              min="0"
                              max="999"
                            />
                            <button
                              onClick={() => handleQuantityChange(item.productId, qty + 1)}
                              disabled={isDisabled}
                              className="w-8 h-8 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                            >
                              +
                            </button>
                          </div>
                        </td>
                        {!hidePricing && (
                        <td className="px-4 py-3 text-right">
                          {(() => {
                            const erpLine = getErpLine(item.productId);
                            let lineTotal;
                            if (erpLine) {
                              // Scale lineTotal by actual qty vs ERP qty
                              lineTotal = (erpLine.lineTotal / (erpLine.quantity || 1)) * qty;
                            } else {
                              lineTotal = item.currentPrice * qty;
                              const pd = myProductDiscounts[key];
                              if (pd) lineTotal *= (1 - pd.percentage / 100);
                            }
                            return (
                              <p className="font-medium">
                                €{lineTotal.toFixed(2)}
                              </p>
                            );
                          })()}
                        </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          {!hidePricing && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">
              {t('business:reorder.modal.summary.title')}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {t('business:reorder.modal.summary.subtotal')}
                </span>
                <span>€{adjustedTotals.subtotal.toFixed(2)}</span>
              </div>
              {adjustedTotals.totalDiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">
                    {t('business:myOrders.discount', 'Discount')}
                  </span>
                  <span className="text-green-600">-€{adjustedTotals.totalDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {t('business:reorder.modal.summary.deliveryFee')}
                </span>
                <span>€{adjustedTotals.deliveryFee.toFixed(2)}</span>
              </div>
              <div className="border-t border-gray-300 pt-2 mt-2">
                <div className="flex justify-between font-semibold text-lg">
                  <span>{t('business:reorder.modal.summary.currentTotal')}</span>
                  <span className={parseFloat(totals.difference) > 0 ? 'text-orange-600' : 'text-green-600'}>
                    €{adjustedTotals.total.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 mt-1">
                  <span>{t('business:reorder.modal.summary.originalTotal')}</span>
                  <span className="line-through">€{parseFloat(totals.originalTotal).toFixed(2)}</span>
                </div>
                <p className="text-xs text-gray-500 italic mt-2">{t('business:cart.vatDisclaimer')}</p>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {wholesaler.companyName}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                disabled={adding}
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                onClick={handleAddToCart}
                disabled={adding}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <ShoppingCart className="w-4 h-4" />
                {adding 
                  ? t('business:reorder.modal.actions.adding') || 'Adding...'
                  : t('business:reorder.modal.actions.addToCart') || 'Add to Cart'
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReorderModal;